import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import {
  canonicalJson,
  FindingStatusSchema,
  SeveritySchema,
  shortDigest,
  type Report,
} from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';
import { Icon } from '../../components/Icon';
import { STATUS_LABEL } from './FindingsList';

const date = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const person = (email: string | undefined, userId: string) => email ?? `user ${userId.slice(0, 8)}`;

/**
 * A report, as published.
 *
 * Everything here is rendered from the stored snapshot, not from live data: if a finding is
 * triaged again tomorrow this page still shows what was submitted and approved. Every string
 * that came from a target — titles, URLs, evidence — is rendered as text, never as markup.
 *
 * The integrity line is the server's verdict on this read: the stored bytes re-hashed and
 * compared with the digest recorded at generation, and with the one the approver recorded.
 */
export function ReportView() {
  const { id = '', version = '' } = useParams();
  const n = Number(version);

  const report = useQuery({
    queryKey: ['report', id, n],
    queryFn: () => api.getReport(id, n),
    enabled: id.length > 0 && Number.isInteger(n) && n > 0,
  });

  if (report.isLoading) {
    return (
      <div className="page">
        <p className="muted">Loading report…</p>
      </div>
    );
  }
  if (report.isError || !report.data) {
    return (
      <div className="page">
        <div className="card empty">
          <h1 className="serif" style={{ fontSize: 32 }}>
            Could not load this report
          </h1>
          {report.error instanceof ApiError ? (
            <p className="muted">{report.error.message}</p>
          ) : null}
          <Link to={`/app/engagements/${id}`} className="btn btn-outline">
            Back to the engagement
          </Link>
        </div>
      </div>
    );
  }

  const { meta, report: r, integrity } = report.data;
  const decision = [...r.approvals]
    .reverse()
    .find((a) => a.gate === 'REPORT' && a.reportVersion === r.version);

  const download = () => {
    // The canonical form, so `sha256sum` on the saved file reproduces the digest shown here.
    const blob = new Blob([canonicalJson(r)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${r.engagement.name.replace(/[^\w.-]+/g, '-')}-report-v${r.version}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page report">
      <Link
        to={`/app/engagements/${id}`}
        className="muted no-print"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
      >
        <Icon name="back" size={16} /> {r.engagement.name}
      </Link>

      <div className="page-head">
        <div>
          <span className="eyebrow">
            Technical report · v{r.version} · {r.engagement.environment}
          </span>
          <h1>{r.engagement.name}</h1>
          <p className="muted" style={{ margin: 0 }}>
            {r.engagement.applicationName ? `${r.engagement.applicationName} · ` : ''}
            Generated {date(r.generatedAt)} by {person(r.generatedByEmail, r.generatedByUserId)}
          </p>
        </div>
        <div className="no-print" style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-outline btn-sm" onClick={download}>
            Download JSON
          </button>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => window.print()}>
            Print
          </button>
        </div>
      </div>

      <IntegrityLine
        digest={meta.digest}
        intact={integrity.intact}
        approvedMatches={integrity.approvedDigestMatches}
        decision={decision}
      />

      <Summary report={r} />

      <section className="card panel">
        <h2 className="panel-title">Scope v{r.scope.version}</h2>
        <dl className="kv">
          <dt>Allowed hosts</dt>
          <dd className="mono">{r.scope.targets.allowedHosts.join(', ')}</dd>
          <dt>Seed URLs</dt>
          <dd className="mono">{r.scope.targets.seedUrls.join(', ')}</dd>
          <dt>Windows</dt>
          <dd>
            {r.scope.windows.map((w) => (
              <div key={w.startsAt}>
                {date(w.startsAt)} → {date(w.endsAt)}
              </div>
            ))}
          </dd>
          <dt>Limits</dt>
          <dd>
            {r.scope.limits.maxRequestsPerSecond} req/s · {r.scope.limits.maxDurationMinutes}{' '}
            minutes
          </dd>
          <dt>Permits</dt>
          <dd>
            Active scanning {r.scope.permits.activeScan ? 'permitted' : 'not permitted'} · fuzzing{' '}
            {r.scope.permits.fuzzing ? 'permitted' : 'not permitted'}
          </dd>
          <dt>Authorisation</dt>
          <dd>
            {r.scope.authorisation.reference} · {r.scope.authorisation.authorisedByName} ·{' '}
            {date(r.scope.authorisation.authorisedAt)}
          </dd>
          <dt>Scope digest</dt>
          <dd className="mono">{shortDigest(r.scope.digest)}</dd>
        </dl>
      </section>

      <section className="card panel table-wrap">
        <h2 className="panel-title">Decisions</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Gate</th>
              <th>Decision</th>
              <th>By</th>
              <th>When</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {r.approvals.map((a) => (
              <tr key={`${a.gate}-${a.at}`}>
                <td>
                  {a.gate}
                  {a.reportVersion ? ` · v${a.reportVersion}` : ` · scope v${a.scopeVersion}`}
                </td>
                <td>{a.decision === 'APPROVED' ? 'Approved' : 'Declined'}</td>
                <td>{person(a.approverEmail, a.approverUserId)}</td>
                <td className="muted">{date(a.at)}</td>
                <td className="muted">{a.note ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card panel table-wrap">
        <h2 className="panel-title">Scans</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Kind</th>
              <th>Status</th>
              <th>Started</th>
              <th>Finished</th>
              <th>Findings</th>
            </tr>
          </thead>
          <tbody>
            {r.scans.map((s) => (
              <tr key={s.id}>
                <td>{s.kind}</td>
                <td>{s.status}</td>
                <td className="muted">{s.startedAt ? date(s.startedAt) : '—'}</td>
                <td className="muted">{s.finishedAt ? date(s.finishedAt) : '—'}</td>
                <td>{s.findingCount ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card panel">
        <h2 className="panel-title">Findings</h2>
        {r.findings.length === 0 ? (
          <p className="muted">No findings. The scans in scope reported nothing.</p>
        ) : (
          <ol className="report-findings">
            {r.findings.map((f) => (
              <li key={f.id} className="report-finding">
                <div className="report-finding-head">
                  <span className={`sev-${f.severity.toLowerCase()}`}>{f.severity}</span>
                  <strong>{f.title}</strong>
                  <span className={`finding-status fs-${f.status.toLowerCase()}`}>
                    {STATUS_LABEL[f.status]}
                  </span>
                </div>
                {f.evidence?.url ? (
                  <p className="mono muted" style={{ fontSize: 12, wordBreak: 'break-all' }}>
                    {f.evidence.method ? `${f.evidence.method} ` : ''}
                    {f.evidence.url}
                  </p>
                ) : null}
                {f.description ? <p>{f.description}</p> : null}
                {f.evidence?.excerpt ? (
                  <pre className="report-excerpt">{f.evidence.excerpt}</pre>
                ) : null}
                {f.remediation ? (
                  <p>
                    <span className="muted">Remediation: </span>
                    {f.remediation}
                  </p>
                ) : null}
                {f.triage ? (
                  <p className="triage-previous">
                    Decided <strong>{STATUS_LABEL[f.triage.to]}</strong> by{' '}
                    {person(f.triage.byEmail, f.triage.byUserId)} on {date(f.triage.at)}: “
                    {f.triage.reason}”
                  </p>
                ) : null}
                <p className="mono muted" style={{ fontSize: 11 }}>
                  {f.ruleId ? `rule ${f.ruleId} · ` : ''}fingerprint {f.fingerprint}
                </p>
              </li>
            ))}
          </ol>
        )}
      </section>

      {r.triage.length > 0 ? (
        <section className="card panel table-wrap">
          <h2 className="panel-title">Triage log</h2>
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Finding</th>
                <th>Change</th>
                <th>By</th>
                <th>Reason</th>
                <th>AI suggestion</th>
              </tr>
            </thead>
            <tbody>
              {r.triage.map((t) => (
                <tr key={`${t.findingId}-${t.at}`}>
                  <td className="muted">{date(t.at)}</td>
                  <td>{r.findings.find((f) => f.id === t.findingId)?.title ?? t.fingerprint}</td>
                  <td>
                    {STATUS_LABEL[t.from]} → {STATUS_LABEL[t.to]}
                  </td>
                  <td>{person(t.byEmail, t.byUserId)}</td>
                  <td>{t.reason}</td>
                  <td className="muted">
                    {t.aiAssist
                      ? `${STATUS_LABEL[t.aiAssist.suggestedStatus]} ${t.aiAssist.confidence}/10 · ${t.aiAssist.followed ? 'followed' : 'overridden'}`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </div>
  );
}

function IntegrityLine({
  digest,
  intact,
  approvedMatches,
  decision,
}: {
  digest: string;
  intact: boolean;
  approvedMatches: boolean | undefined;
  decision: Report['approvals'][number] | undefined;
}) {
  if (!intact) {
    return (
      <p className="login-error" role="alert">
        This report no longer matches the digest recorded when it was generated (
        <span className="mono">{shortDigest(digest)}</span>). Its stored content has changed.
      </p>
    );
  }
  if (approvedMatches === false) {
    return (
      <p className="login-error" role="alert">
        This report does not match the digest its approver recorded. What is stored is not what was
        approved.
      </p>
    );
  }
  return (
    <p className={`integrity-banner ${approvedMatches ? 'is-approved' : ''}`} role="status">
      <span className="mono">sha256 {shortDigest(digest)}</span> ·{' '}
      {approvedMatches && decision
        ? `Unchanged since ${decision.decision === 'APPROVED' ? 'approved' : 'declined'} by ${person(decision.approverEmail, decision.approverUserId)} on ${date(decision.at)}`
        : 'Unchanged since generated · awaiting a decision'}
    </p>
  );
}

function Summary({ report }: { report: Report }) {
  const severities = SeveritySchema.options;
  const statuses = FindingStatusSchema.options;
  return (
    <section className="card panel">
      <h2 className="panel-title">Summary</h2>
      <div className="report-summary">
        <div>
          <span className="report-total">{report.summary.total}</span>
          <span className="muted">findings</span>
        </div>
        <dl className="report-counts">
          {severities.map((s) => (
            <div key={s}>
              <dt className={`sev-${s.toLowerCase()}`}>{s.charAt(0) + s.slice(1).toLowerCase()}</dt>
              <dd>{report.summary.bySeverity[s]}</dd>
            </div>
          ))}
        </dl>
        <dl className="report-counts">
          {statuses.map((s) => (
            <div key={s}>
              <dt>{STATUS_LABEL[s]}</dt>
              <dd>{report.summary.byStatus[s]}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
